import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Presentation, Search, Download, Loader2, Eye } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const TALEN = [
  { v: "nl", label: "Nederlands" },
  { v: "en", label: "Engels" },
  { v: "de", label: "Duits" },
  { v: "fr", label: "Frans" },
];

export function PresentatieDialog({ orgId, orgNaam }) {
  const [open, setOpen] = useState(false);
  const [producten, setProducten] = useState([]);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(new Set());
  const [taal, setTaal] = useState("nl");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const openVoorbeeld = () => {
    if (sel.size === 0) { toast.error("Selecteer minimaal één product"); return; }
    if (sel.size > 30) { toast.error("Maximaal 30 producten"); return; }
    const ids = [...sel].join(",");
    setOpen(false);
    navigate(`/organisaties/${orgId}/presentatie-voorbeeld?producten=${ids}&taal=${taal}`);
  };

  useEffect(() => {
    if (open && producten.length === 0) {
      api.get("/producten").then((r) => setProducten(r.data || [])).catch(() => toast.error("Producten laden mislukt"));
    }
  }, [open]); // eslint-disable-line

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return producten;
    return producten.filter((p) => `${p.naam || ""} ${p.sku || ""}`.toLowerCase().includes(s));
  }, [producten, q]);

  const toggle = (id) => setSel((x) => { const n = new Set(x); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const alleZichtbare = () => setSel((x) => {
    const alle = filtered.every((p) => x.has(p.id));
    const n = new Set(x);
    filtered.forEach((p) => alle ? n.delete(p.id) : n.add(p.id));
    return n;
  });

  const genereer = async () => {
    if (sel.size === 0) { toast.error("Selecteer minimaal één product"); return; }
    if (sel.size > 30) { toast.error("Maximaal 30 producten per presentatie"); return; }
    setBusy(true);
    // Verwachte tijd: ~1s per product + 4s vertaal (indicatief; AI-tekstgeneratie kan langer duren)
    const geschat = sel.size <= 3 ? "5 – 10 seconden" : sel.size <= 8 ? "10 – 25 seconden" : "20 – 60 seconden";
    const toastId = toast.loading(`Presentatie wordt gegenereerd (± ${geschat})…`, {
      description: "AI vertaalt teksten en genereert beschrijvingen waar nodig.",
    });
    try {
      const { data } = await api.post(`/organisaties/${orgId}/presentatie`,
        { product_ids: [...sel], taal }, { responseType: "blob", timeout: 300000 });
      const blob = data instanceof Blob
        ? data
        : new Blob([data], { type: "application/vnd.openxmlformats-officedocument.presentationml.presentation" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `assortiment-${(orgNaam || "relatie").toLowerCase().replace(/[^\w-]+/g, "-")}-${taal}.pptx`;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 1500);
      toast.success("Presentatie gedownload", { id: toastId });
      setOpen(false);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Genereren mislukt";
      toast.error(msg, { id: toastId });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="presentatie-btn">
          <Presentation className="h-4 w-4 mr-1" /> Presentatie
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Assortimentspresentatie (PPTX)</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <Label className="text-xs text-muted-foreground">Zoek product</Label>
              <div className="relative">
                <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
                <Input className="pl-8" value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="Naam of SKU…" data-testid="presentatie-zoek" />
              </div>
            </div>
            <div className="w-40">
              <Label className="text-xs text-muted-foreground">Taal</Label>
              <Select value={taal} onValueChange={setTaal}>
                <SelectTrigger data-testid="presentatie-taal"><SelectValue /></SelectTrigger>
                <SelectContent>{TALEN.map((t) => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center justify-between text-sm">
            <button className="text-primary hover:underline" onClick={alleZichtbare} data-testid="presentatie-selecteer-alle">
              {filtered.length > 0 && filtered.every((p) => sel.has(p.id)) ? "Deselecteer zichtbare" : "Selecteer zichtbare"}
            </button>
            <span className="text-muted-foreground" data-testid="presentatie-aantal">{sel.size} geselecteerd</span>
          </div>
          <div className="border border-border rounded-lg max-h-72 overflow-y-auto divide-y divide-border" data-testid="presentatie-lijst">
            {filtered.map((p) => (
              <label key={p.id} className="flex items-center gap-3 p-2.5 hover:bg-secondary cursor-pointer">
                <Checkbox checked={sel.has(p.id)} onCheckedChange={() => toggle(p.id)}
                  data-testid={`presentatie-product-${p.id}`} />
                <span className="text-sm flex-1">{p.naam}</span>
                <span className="text-xs text-muted-foreground">{p.sku}</span>
              </label>
            ))}
            {filtered.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">Geen producten gevonden.</div>}
          </div>
          <p className="text-xs text-muted-foreground">
            16:9 presentatie met logo, "Over ons" (uit bedrijfsinstellingen), productpagina's met klantspecifieke prijzen en een bestellijst.
            Bij een andere taal worden bestaande productteksten automatisch vertaald met AI. Producten zonder beschrijving in het CRM
            blijven leeg — vul de productteksten in als je ze in de presentatie wilt zien. Max. 30 producten per presentatie.
          </p>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Annuleren</Button>
          <Button variant="outline" onClick={openVoorbeeld} disabled={busy || sel.size === 0} data-testid="presentatie-voorbeeld">
            <Eye className="h-4 w-4 mr-2" /> Voorbeeld
          </Button>
          <Button onClick={genereer} disabled={busy} data-testid="presentatie-genereer">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
            {busy ? "Genereren…" : "Genereer & download"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
